import React, { createContext, useCallback, useContext, useState } from "react";
import { tripsService } from "../api/tripsService/tripsServices";
import {
  SendTripDocumentRequest,
  SendTripDocumentResponse,
  TripPODApiResponse,
  TripPODResponse,
} from "../types/api";
// import { appInsights, trackFullPayload } from "../utils/appInsights";

interface TripDocumentContextType {
  tripDocuments: TripPODResponse[] | null;
  isDocumentLoading: boolean;
  isPackingListLoading: boolean;
  errorMessage: string | null;
  uploadPOD: (file: File) => Promise<any>;
  uploadResponse: any | null;
  sendTripDocument: (payload: SendTripDocumentRequest) => Promise<void>;
  tripDocumentResponse: SendTripDocumentResponse | null;
  fetchLatestPODDoc: (payload: any) => Promise<TripPODResponse[] | null>;
  fetchLatestPKLDoc: (payload: any) => Promise<TripPODResponse[] | null>;
  getDocumentDetails: (documentId: string) => Promise<any>;
  getTermsAndConditions: (
    accountId: string,
    contactId: string,
    lang: string,
    readonly?: boolean
  ) => Promise<any>;
  termsAndConditions: any | null; 
  termsCondError:string | null;
  termsCondLoader:boolean;

  generatePDF: (tripId: number, legacyCode: string, language: string, contactId:string, accountId:string, company:string) => Promise<string>;

}

interface TripDocumentProviderProps {
  children: React.ReactNode;
}

const TripDocumentContext = createContext<TripDocumentContextType | undefined>(
  undefined
);

export const TripDocumentProvider: React.FC<TripDocumentProviderProps> = ({
  children,
}) => {
  const [tripDocuments, setTripDocuments] = useState<TripPODResponse[] | null>(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(false);
  const [isPackingListLoading, setIsPackingListLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadResponse, setUploadResponse] = useState<any | null>(null);
  const [tripDocumentResponse, setTripDocumentResponse] =useState<SendTripDocumentResponse | null>(null);
  const [termsAndConditions, setTermsAndConditions] = useState<any | null>(null);
  const [termsCondError, setTermsCondError] = useState<string | null>(null);
  const [termsCondLoader, setTermsCondLoader] = useState<boolean>(false);
  const uploadPOD = async (file: File): Promise<any> => {
    setIsDocumentLoading(true);
    setErrorMessage(null);

    // Traccia l'inizio dell'upload del documento
    // appInsights.trackEvent({
    //   name: 'Document_Upload_Started',
    //   properties: {
    //     fileName: file.name,
    //     fileSize: file.size,
    //     fileType: file.type,
    //     lastModified: new Date(file.lastModified).toISOString()
    //   }
    // });

    try {
      const formData = new FormData();
      formData.append("file", file);
      
      // Traccia il payload dell'upload (metadati del file)
      // const uploadPayload = {
      //   fileName: file.name,
      //   fileSize: file.size,
      //   fileType: file.type,
      //   lastModified: new Date(file.lastModified).toISOString(),
      //   formDataKeys: Array.from(formData.keys())
      // };
      
      // trackFullPayload('Document_Upload_Payload', uploadPayload, {
      //   payloadType: 'Document_Upload',
      //   hasFile: !!file,
      //   fileSizeBytes: file.size
      // });
      
      const response = await tripsService.uploadPOD({ file: formData });
      if (response) {
        setUploadResponse(response);
        
        // Traccia il successo dell'upload
        // appInsights.trackEvent({
        //   name: 'Document_Upload_Success',
        //   properties: {
        //     bufferIds: response,
        //     bufferIdsCount: Array.isArray(response) ? response.length : 0,
        //     hasBufferIds: Array.isArray(response) && response.length > 0
        //   }
        // });
        
        return response;
      } else {
        throw new Error("Risposta non valida o bufferIds vuoto.");
      }
    } catch (error: any) {
      setErrorMessage(
        error.message || "Errore durante il caricamento del documento."
      );
      
      // Traccia l'errore dell'upload
      // appInsights.trackEvent({
      //   name: 'Document_Upload_Error',
      //   properties: {
      //     errorMessage: error.message,
      //     errorResponse: error.response?.data
      //   }
      // });
      
      return { bufferIds: [] };
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const sendTripDocument = async (payload: any) => {
    console.log('[DEBUG] TripDocumentContext.sendTripDocument called with payload:', payload);
    setIsDocumentLoading(true);
    setErrorMessage(null);
    
    // Traccia il payload completo per l'invio del documento
    // trackFullPayload('Document_Send_Payload', payload, {
    //   payloadType: 'Document_Send',
    //   hasBufferIds: !!payload.document?.bufferIds?.length,
    //   bufferIdsCount: payload.document?.bufferIds?.length || 0,
    //   fieldsCount: payload.fields?.length || 0,
    //   documentType: payload.fields?.find((f: any) => f.name === 'DocumentType')?.value,
    //   documentName: payload.fields?.find((f: any) => f.name === 'DocName')?.value,
    //   fileName: payload.fields?.find((f: any) => f.name === 'Originale')?.value
    // });
    
    try {
      console.log('[DEBUG] TripDocumentContext calling tripsService.sendTripDocument...');
      const response = await tripsService.sendTripDocument(payload);
      console.log('[DEBUG] TripDocumentContext received response:', response);
      setTripDocumentResponse(response);
      
      // Traccia il successo dell'invio del documento
      // appInsights.trackEvent({
      //   name: 'Document_Send_Success',
      //   properties: {
      //     docNumber: response?.docNumber,
      //     protocolNumber: response?.protocolNumber,
      //     internalProtocolNumber: response?.internalProtocolNumber,
      //     year: response?.year,
      //     showMessage: response?.showMessage
      //   }
      // });
    } catch (error: any) {
      console.error('[DEBUG] TripDocumentContext.sendTripDocument error:', error);
      setErrorMessage(error.message || "Errore durante l'invio del documento.");
      
      // Traccia l'errore nell'invio del documento
      // appInsights.trackEvent({
      //   name: 'Document_Send_Error',
      //   properties: {
      //     errorMessage: error.message,
      //     errorResponse: error.response?.data
      //   }
      // });
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const fetchLatestPODDoc = async (payload: any): Promise<TripPODResponse[] | null> => {
    setIsDocumentLoading(true);
    setErrorMessage(null);

    // Traccia l'inizio del fetch dei documenti
    // appInsights.trackEvent({
    //   name: 'Document_Fetch_Started',
    //   properties: {
    //     payloadType: 'Document_Fetch',
    //     hasPayload: !!payload
    //   }
    // });

    // Traccia il payload completo per il fetch dei documenti
    // trackFullPayload('Document_Fetch_Payload', payload, {
    //   payloadType: 'Document_Fetch',
    //   searchFilterCount: payload?.searchFilterDtoList?.length || 0,
    //   selectFilterCount: payload?.selectFilterDto?.fields?.length || 0,
    //   maxItems: payload?.selectFilterDto?.maxItems
    // });

    try {
      const response: TripPODApiResponse = await tripsService.getPODDocNumbers(payload);
      if (response && response.columns && response.data) {
        // Mappa tutte le righe dei dati in oggetti TripPODResponse
        const documents: TripPODResponse[] = response.data.map((row: any[]) => {
          const resultObj: any = {};
          response.columns.forEach((col: any, index: number) => {
            resultObj[col.id] = row[index];
          });
          console.log("resultObj",resultObj)
          return resultObj as TripPODResponse;
        });
        
        // Imposta i documenti nel context
        setTripDocuments(documents);
        
        // Traccia il successo del fetch
        // appInsights.trackEvent({
        //   name: 'Document_Fetch_Success',
        //   properties: {
        //     documentsCount: documents.length,
        //     hasDocuments: documents.length > 0,
        //     columnsCount: response.columns?.length || 0,
        //     dataRowsCount: response.data?.length || 0
        //   }
        // });
        
        // Se non ci sono documenti, lancia un errore
        if (documents.length === 0) {
          throw new Error("Nessun documento trovato.");
        }

        return documents; // Restituisce tutti i documenti
      } else {
        throw new Error("Formato della risposta non valido.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Errore durante il recupero dei documenti.");
      
      // Traccia l'errore del fetch
      // appInsights.trackEvent({
      //   name: 'Document_Fetch_Error',
      //   properties: {
      //     errorMessage: error.message,
      //     errorResponse: error.response?.data
      //   }
      // });
      
      return null;
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const fetchLatestPKLDoc = async (payload: any): Promise<TripPODResponse[] | null> => {
    setIsPackingListLoading(true);
    setErrorMessage(null);

    try {
      const response: TripPODApiResponse = await tripsService.getPODDocNumbers(payload);
      if (response && response.columns && response.data) {
        // Mappa tutte le righe dei dati in oggetti TripPODResponse
        const documents: TripPODResponse[] = response.data.map((row: any[]) => {
          const resultObj: any = {};
          response.columns.forEach((col: any, index: number) => {
            resultObj[col.id] = row[index];
          });
          return resultObj as TripPODResponse;
        });
        
        // Se non ci sono documenti, restituisce array vuoto invece di lanciare errore
        if (documents.length === 0) {
          return [];
        }

        return documents; // Restituisce tutti i documenti
      } else {
        return [];
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Errore durante il recupero dei documenti PKL.");
      return null;
    } finally {
      setIsPackingListLoading(false);
    }
  };

  const getDocumentDetails = async (documentId: string): Promise<any> => {
    setIsDocumentLoading(true);
    setErrorMessage(null);
  
    try {
      const documentDetails = await tripsService.getDocumentDetails2(documentId);
      return documentDetails;
    } catch (error: any) {
      setErrorMessage(error.message || "Errore durante il recupero del documento.");
      throw error;
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const getTermsAndConditions = async (
    accountId: string,
    contactId: string,
    lang: string,
    readonly: boolean = false
  ): Promise<any> => {
    setTermsCondLoader(true);
    setTermsCondError(null);
  
    try {
      const terms = await tripsService.getTermsAndConditions(accountId, contactId, lang, readonly);
      setTermsAndConditions(terms); // Salviamo la risposta nello stato
      return terms;
    } catch (error: any) {
      setTermsCondError(error.message || "Errore durante il recupero dei Termini e Condizioni.");
      throw error;
    } finally {
      setTermsCondLoader(false);
    }
  };


  const generatePDF = async (tripId: number, legacyCode: string, language: string, contactId: string, accountId: string, company: string) => {
    try {
      // Chiamata al servizio per ottenere i dati del PDF come ArrayBuffer
      const response = await tripsService.generateTransportPDF({ tripId, legacyCode, language, contactId, accountId, company });
      
      if (!response || !response.data) {
        throw new Error("Failed to generate PDF data");
      }
  
      // Decodifica la stringa base64 in un ArrayBuffer
      const pdfData = atob(response.data);
      const byteNumbers = new Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        byteNumbers[i] = pdfData.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
  
      // Crea un Blob dal ArrayBuffer
      const blob = new Blob([byteArray], { type: 'application/pdf' });
  
      // Crea un URL per il PDF
      const pdfUrl = URL.createObjectURL(blob);
  
      return pdfUrl;
    } catch (error) {
      console.error("Error generating transport PDF in context:", error);
      throw error;
    }
  };
  

  return (
    <TripDocumentContext.Provider
      value={{
        tripDocuments,
        isDocumentLoading,
        isPackingListLoading,
        errorMessage,
        uploadPOD,
        uploadResponse,
        sendTripDocument,
        tripDocumentResponse,
        fetchLatestPODDoc,
        fetchLatestPKLDoc,
        getDocumentDetails ,
        termsAndConditions,
        getTermsAndConditions,
        termsCondError,
        termsCondLoader,
        generatePDF
      }}
    >
      {children}
    </TripDocumentContext.Provider>
  );
};

export const useTripDocument = (): TripDocumentContextType => {
  const context = useContext(TripDocumentContext);
  if (!context) {
    throw new Error(
      "useTripDocument deve essere utilizzato all'interno di un TripDocumentProvider"
    );
  }
  return context;
};
